import React, { useState, useEffect, useContext } from 'react';
import UserContext from '../../components/User_Context.js';
import * as menu from "../../assets/menus/menu.js"
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import * as api from "../../api/apiConnector.js";
import waiting from "../../assets/images/waiting.svg";
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import * as utils from "../../components/utils.js";
import { useTranslation } from 'react-i18next';
import { SystemLabels } from '../_label/system/system_labels.js';
import Select from 'react-select';

const LIS_ALERT_CONFIGURATION = process.env.REACT_APP_LIS_ALERT_CONFIGURATION === 'true';

const Configurations_Screen = () => {
    const { currentUser } = useContext(UserContext);
    const [configuration, setConfiguration] = useState(null);
    const [originalConfiguration, setOriginalConfiguration] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const { t } = useTranslation();

    // ── Gestione ICA (ruoli organizzativi) ────────────────────────────────────
    const [icaRoles, setIcaRoles] = useState([]);
    const [icaSchedaId, setIcaSchedaId] = useState('');
    const [organizationalRolesVoc, setOrganizationalRolesVoc] = useState([]);
    const [vocLoading, setVocLoading] = useState(false);

    // ── Gestione Alert (gruppi) ────────────────────────────────────────────────
    const [alertGroups, setAlertGroups] = useState([]);
    const [alertSchedaId, setAlertSchedaId] = useState('');
    const [groupsVoc, setGroupsVoc] = useState([]);
    const [groupsVocLoading, setGroupsVocLoading] = useState(false);

    // ── Schede disponibili ────────────────────────────────────────────────────
    const [schedeVoc, setSchedeVoc] = useState([]);
    const [schedeVocLoading, setSchedeVocLoading] = useState(false);

    // Initialize i18n labels from centralized file
    const i18n = menu.getI18n();
    i18n.addResourceBundle('it', 'labels', SystemLabels.it, true, true);
    i18n.addResourceBundle('en', 'labels', SystemLabels.en, true, true);

    // Fallback descriptions for configuration fields
    const getFieldDescription = (fieldName) => {
        const currentLanguage = i18n.language || 'it';

        const descriptions = {
            it: {
                'ShowType': 'Mostra il pulsante Tipo "Interno/Esterno"',
                'ACPM': 'Mostra il pulsante ACPM nel campo note',
                'ShowGender': 'Mostra il pulsante Genere "Uomo/Donna"',
                                'LISAllowSendAlert': 'Abilita l\'invio delle notifiche di alert LIS',
                'AllowMultipleIndications': 'Consente di abilitare/disabilitare indicazioni multiple nell\'interfaccia utente',
                'HospitalizationImportType': t('hospitalizationImportTypeDescription', { ns: 'labels' }) || 'Tracciato di importazione per le degenze (ADT = eventi singoli, Riepilogo mensile = dati aggregati per mese)'
            },
            en: {
                'ShowType': 'Show type bit "Internal/External"',
                'ACPM': 'Show ACPM bit in notes field',
                'ShowGender': 'Show gender bit "Male/Female"',
                                'LISAllowSendAlert': 'Enable sending LIS alert notifications',
                'AllowMultipleIndications': 'Allow/disable multiple indications in the user interface',
                'HospitalizationImportType': t('hospitalizationImportTypeDescription', { ns: 'labels' }) || 'Hospitalization import format (ADT = single events, Monthly summary = monthly aggregated data)'
            }
        };

        return descriptions[currentLanguage]?.[fieldName] || descriptions['it'][fieldName] || fieldName;
    };

    const setBreadCrumb = () => {
        var path = [{
            link: "/",
            name: t('home', { ns: 'labels' })
        },
        {
            link: "/admin",
            name: t('admin', { ns: 'labels' })
        },
        {
            link: "/admin/configurations",
            name: t('configurations', { ns: 'labels' })
        }
        ];
        breadcrumb.set(path);
    }

    const loadOrganizationalRoles = async () => {
        setVocLoading(true);
        try {
            const res = await api.sendMessage('admin/vocs/get', {
                user: currentUser(),
                body: { voc_id: 'organizational_chart_roles' }
            });
            if (res.success) {
                setOrganizationalRolesVoc(
                    (res.items || []).map(r => ({ value: r.Code, label: r.Name }))
                );
            }
        } catch (e) {
            console.error('Error loading organizational roles:', e);
        } finally {
            setVocLoading(false);
        }
    };

    const loadGroups = async () => {
        setGroupsVocLoading(true);
        try {
            const res = await api.sendMessage('usersGroups/search', {
                user: currentUser(),
                body: { name: '', email: '', userName: '', isDisabled: false }
            });
            if (res.success) {
                setGroupsVoc(
                    (res.filtered?.groups || []).map(g => ({ value: g.Id, label: g.Name }))
                );
            }
        } catch (e) {
            console.error('Error loading groups:', e);
        } finally {
            setGroupsVocLoading(false);
        }
    };

    const loadSchede = async () => {
        setSchedeVocLoading(true);
        try {
            const res = await api.sendMessage('darm/formBuilder/finder', {
                user: currentUser(),
                body: { name: '', code: '', group: '' }
            });
            if (res.success) {
                setSchedeVoc(
                    (res.filtered || []).map(s => ({ value: s.Id, label: s.FormName || s.Code }))
                );
            }
        } catch (e) {
            console.error('Errore caricamento schede:', e);
        } finally {
            setSchedeVocLoading(false);
        }
    };

    useEffect(() => {
        setBreadCrumb();
        menu.write({ user: currentUser() });
        loadConfiguration();
        if (LIS_ALERT_CONFIGURATION) {
            loadOrganizationalRoles();
            loadGroups();
            loadSchede();
        }
    }, []);

    // Populate alert tables when config is first loaded (or reloaded after save)
    useEffect(() => {
        if (!originalConfiguration || !LIS_ALERT_CONFIGURATION) return;
        const configData = originalConfiguration.config || {};
        const gICA   = configData.GestioneICA   || {};
        const gAlert = configData.GestioneAlert || {};

        setIcaRoles(
            (gICA.Roles || []).map((item, idx) => ({
                _key: idx,
                roleId: item.Code || '',
                warn:   !!item.Warn,
                scheda: !!item.Scheda
            }))
        );
        setIcaSchedaId(gICA.SchedaId || '');

        setAlertGroups(
            (gAlert.Groups || []).map((item, idx) => ({
                _key: idx + 10000,
                groupId: item.Id || '',
                warn:    !!item.Warn,
                scheda:  !!item.Scheda
            }))
        );
        setAlertSchedaId(gAlert.SchedaId || '');
    }, [originalConfiguration]);

    const loadConfiguration = async () => {
        setIsLoading(true);
        try {
            const response = await api.sendMessage('darm/configurations/get', {
                user: currentUser(),
                body: {}
            });

            if (response.success) {
                const data = response.data;
                // Ensure HospitalizationImportType is always present (tenants created before
                // this field was added won't have it in the DB — inject the default here so
                // it shows up and gets persisted on the first save).
                if (data.config && data.config.HospitalizationImportType === undefined) {
                    data.config = { ...data.config, HospitalizationImportType: 'ADT' };
                }
                if (data.config && data.config.LISAllowSendAlert === undefined) {
                    data.config = { ...data.config, LISAllowSendAlert: false };
                }
                setConfiguration(data);
                setOriginalConfiguration(JSON.parse(JSON.stringify(data))); // Deep copy for comparison
                setHasUnsavedChanges(false);
            } else {
                console.error('Error from API:', response);
                showError(t('errorLoadingConfiguration', { ns: 'labels' }));
            }
        } catch (error) {
            console.error('Error loading configuration:', error);
            showError(t('errorLoadingConfiguration', { ns: 'labels' }));
        } finally {
            setIsLoading(false);
        }
    };

    const toggleField = (fieldName) => {
        if (!configuration) return;

        // Update local state only (no API call)
        const updatedConfig = { ...configuration };
        if (updatedConfig.config && typeof updatedConfig.config === 'object') {
            updatedConfig.config = { ...updatedConfig.config };
            updatedConfig.config[fieldName] = !updatedConfig.config[fieldName];
        } else {
            // Fallback for string format
            const configData = JSON.parse(updatedConfig.Config || '{}');
            configData[fieldName] = !configData[fieldName];
            updatedConfig.Config = JSON.stringify(configData);
        }
        setConfiguration(updatedConfig);
        
        // Check if there are unsaved changes
        checkForUnsavedChanges(updatedConfig);
    };

    const setFieldValue = (fieldName, value) => {
        if (!configuration) return;
        const updatedConfig = { ...configuration };
        if (updatedConfig.config && typeof updatedConfig.config === 'object') {
            updatedConfig.config = { ...updatedConfig.config };
            updatedConfig.config[fieldName] = value;
        } else {
            const configData = JSON.parse(updatedConfig.Config || '{}');
            configData[fieldName] = value;
            updatedConfig.Config = JSON.stringify(configData);
        }
        setConfiguration(updatedConfig);
        checkForUnsavedChanges(updatedConfig);
    };

    const checkForUnsavedChanges = (currentConfig) => {
        if (!originalConfiguration) {
            setHasUnsavedChanges(false);
            return;
        }

        // Compare current configuration with original
        const currentConfigData = currentConfig.config || JSON.parse(currentConfig.Config || '{}');
        const originalConfigData = originalConfiguration.config || JSON.parse(originalConfiguration.Config || '{}');

        const hasChanges = Object.keys(currentConfigData).some(key => 
            currentConfigData[key] !== originalConfigData[key]
        );

        setHasUnsavedChanges(hasChanges);
    };

    const saveConfiguration = async () => {
        const hasAlertChanges = computeHasAlertChanges();
        if (!configuration || (!hasUnsavedChanges && !hasAlertChanges)) {
            showInfo(t('noChangesToSave', { ns: 'labels' }));
            return;
        }

        setIsSaving(true);
        try {
            const configData = configuration.config || JSON.parse(configuration.Config || '{}');

            // Merge alert tables into config payload
            const icaRolesData = icaRoles
                .filter(r => r.roleId)
                .map(r => ({
                    Code:   r.roleId,
                    Name:   organizationalRolesVoc.find(o => o.value === r.roleId)?.label || '',
                    Warn:   r.warn,
                    Scheda: r.scheda
                }));
            const alertGroupsData = alertGroups
                .filter(g => g.groupId)
                .map(g => ({
                    Id:     g.groupId,
                    Name:   groupsVoc.find(o => o.value === g.groupId)?.label || '',
                    Warn:   g.warn,
                    Scheda: g.scheda
                }));

            const mergedConfig = { ...configData };
            if (LIS_ALERT_CONFIGURATION) {
                if (icaRolesData.length > 0 || icaSchedaId) {
                    mergedConfig.GestioneICA = { Roles: icaRolesData, SchedaId: icaSchedaId || null };
                } else {
                    delete mergedConfig.GestioneICA;
                }
                if (alertGroupsData.length > 0 || alertSchedaId) {
                    mergedConfig.GestioneAlert = { Groups: alertGroupsData, SchedaId: alertSchedaId || null };
                } else {
                    delete mergedConfig.GestioneAlert;
                }
                // Rimuove le chiavi del vecchio schema se ancora presenti
                delete mergedConfig.AlertRoles;
                delete mergedConfig.AlertGenericRoles;
            }

            const response = await api.sendMessage('darm/configurations/save', {
                user: currentUser(),
                body: { configuration: mergedConfig }
            });

            if (response.success) {
                const updatedConfiguration = { ...configuration, config: mergedConfig };
                setConfiguration(updatedConfiguration);
                setOriginalConfiguration(JSON.parse(JSON.stringify(updatedConfiguration)));
                setHasUnsavedChanges(false);
                showSuccess(t('configurationSavedSuccessfully', { ns: 'labels' }));
            } else {
                console.error('Save configuration API error:', response);
                showError(t('errorSavingConfiguration', { ns: 'labels' }));
            }
        } catch (error) {
            console.error('Error saving configuration:', error);
            showError(t('errorSavingConfiguration', { ns: 'labels' }));
        } finally {
            setIsSaving(false);
        }
    };

    const showSuccess = (message) => {
        withReactContent(Swal).fire({
            title: t('success', { ns: 'labels' }),
            icon: "success",
            text: message,
            showConfirmButton: false,
            timer: 2000
        });
    };

    const showError = (message) => {
        withReactContent(Swal).fire({
            title: t('error', { ns: 'labels' }),
            icon: "error",
            text: message,
            showConfirmButton: true,
            allowOutsideClick: false,
            showCloseButton: true,
        });
    };

    const showInfo = (message) => {
        withReactContent(Swal).fire({
            title: t('info', { ns: 'labels' }),
            icon: "info",
            text: message,
            showConfirmButton: true,
            timer: 2000
        });
    };

    const computeHasAlertChanges = () => {
        if (!originalConfiguration || !LIS_ALERT_CONFIGURATION) return false;
        const origConfig = originalConfiguration.config || {};
        const origICA    = origConfig.GestioneICA   || {};
        const origAlert  = origConfig.GestioneAlert || {};

        const origIcaRoles = origICA.Roles || [];
        const currIcaRoles = icaRoles.filter(r => r.roleId);
        if (origIcaRoles.length !== currIcaRoles.length) return true;
        if ((origICA.SchedaId || '') !== icaSchedaId) return true;
        for (let i = 0; i < currIcaRoles.length; i++) {
            const o = origIcaRoles[i];
            const c = currIcaRoles[i];
            if ((o?.Code ?? '') !== c.roleId || !!o?.Warn !== c.warn || !!o?.Scheda !== c.scheda) return true;
        }

        const origAlertGroups = origAlert.Groups || [];
        const currAlertGroups = alertGroups.filter(g => g.groupId);
        if (origAlertGroups.length !== currAlertGroups.length) return true;
        if ((origAlert.SchedaId || '') !== alertSchedaId) return true;
        for (let i = 0; i < currAlertGroups.length; i++) {
            const o = origAlertGroups[i];
            const g = currAlertGroups[i];
            if ((o?.Id ?? '') !== g.groupId || !!o?.Warn !== g.warn || !!o?.Scheda !== g.scheda) return true;
        }

        return false;
    };

    const renderConfiguration = () => {
        const ALERT_KEYS = ['GestioneICA', 'GestioneAlert', 'AlertRoles', 'AlertGenericRoles'];

        let configData = {};
        if (configuration) {
            try {
                if (configuration.config && typeof configuration.config === 'object') {
                    configData = configuration.config;
                } else if (configuration.Config && typeof configuration.Config === 'string') {
                    configData = JSON.parse(configuration.Config);
                }
            } catch (error) {
                console.error('Error processing configuration data:', error);
            }
        }

        const visibleFields = Object.entries(configData).filter(([k]) =>
            !ALERT_KEYS.includes(k) &&
            (k !== 'LISAllowSendAlert' || LIS_ALERT_CONFIGURATION)
        );

        const renderConfigFields = () => {
            if (isLoading) {
                return (
                    <div className="text-center py-4">
                        <img src={waiting} alt="Loading..." style={{ width: '50px' }} />
                        <p className="mt-2">{t('loadingConfiguration', { ns: 'labels' })}</p>
                    </div>
                );
            }
            if (!configuration) {
                return (
                    <div className="text-center py-4">
                        <i className="fa fa-info-circle fa-3x text-muted mb-3"></i>
                        <h5 className="text-muted">{t('noConfigurationFound', { ns: 'labels' })}</h5>
                        <button className="btn btn-primary mt-3" onClick={loadConfiguration}>
                            <i className="fa fa-refresh me-2"></i>
                            {t('retry', { ns: 'labels' })}
                        </button>
                    </div>
                );
            }
            if (visibleFields.length === 0) {
                return (
                    <div className="text-center py-5">
                        <div className="text-muted mb-3">
                            <i className="fa fa-cog" style={{ fontSize: '64px', opacity: 0.3 }}></i>
                        </div>
                        <h5 className="text-muted mb-3">{t('noConfigurationFound', { ns: 'labels' })}</h5>
                        <p className="text-muted">{t('noFieldsInConfiguration', { ns: 'labels' })}</p>
                        <button className="btn btn-primary mt-3" onClick={loadConfiguration}>
                            <i className="fa fa-refresh me-2"></i>
                            {t('retry', { ns: 'labels' })}
                        </button>
                    </div>
                );
            }
            return (
                <div className="row">
                    {visibleFields.map(([fieldName, fieldValue]) => (
                        <div key={fieldName} className="col-12 col-lg-6 col-xl-4 mb-4">
                            <div className="card h-100 border-light shadow-sm" style={{ transition: 'all 0.3s ease' }}>
                                <div className="card-body p-4">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div className="flex-grow-1">
                                            <h6 className="fw-semibold mb-2 text-dark">{fieldName}</h6>
                                            <p className="text-muted small mb-2" style={{ lineHeight: '1.4' }}>
                                                {getFieldDescription(fieldName)}
                                            </p>
                                            <div className="d-flex align-items-center">
                                                {typeof fieldValue === 'boolean' ? (
                                                    <span className={`badge ${fieldValue ? 'bg-success' : 'bg-secondary'} me-2`} style={{ fontSize: '0.7em' }}>
                                                        <i className={`fa ${fieldValue ? 'fa-check' : 'fa-times'} me-1`}></i>
                                                        {fieldValue ? t('enabled', { ns: 'labels' }) : t('disabled', { ns: 'labels' })}
                                                    </span>
                                                ) : fieldName === 'HospitalizationImportType' ? (
                                                    <span className="badge bg-info me-2" style={{ fontSize: '0.7em' }}>
                                                        <i className="fa fa-hospital me-1"></i>
                                                        {fieldValue === 'MONTHLY' ? 'Riepilogo mensile' : 'ADT'}
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-light text-dark border me-2" style={{ fontSize: '0.7em' }}>
                                                        {String(fieldValue)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="ms-3">
                                            {typeof fieldValue === 'boolean' ? (
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`switch-${fieldName}`}
                                                        checked={fieldValue}
                                                        onChange={() => toggleField(fieldName)}
                                                        disabled={isSaving}
                                                        style={{ transform: 'scale(1.2)' }}
                                                    />
                                                </div>
                                            ) : fieldName === 'HospitalizationImportType' ? (
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={fieldValue}
                                                    onChange={(e) => setFieldValue(fieldName, e.target.value)}
                                                    disabled={isSaving}
                                                    style={{ minWidth: '160px' }}
                                                >
                                                    <option value="ADT">ADT</option>
                                                    <option value="MONTHLY">Riepilogo mensile</option>
                                                </select>
                                            ) : (
                                                <span className="badge bg-light text-dark border">{String(fieldValue)}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            );
        };

        return (
            <div className="card">
                <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-1">
                                <i className="fa fa-cog me-2 text-primary"></i>
                                {t('configurationSettings', { ns: 'labels' }) !== 'configurationSettings' ? t('configurationSettings', { ns: 'labels' }) : 'Impostazioni di Configurazione'}
                            </h5>
                            {configuration && (
                                <small className="text-muted">
                                    {configuration.healthAuthority || configuration.HealthAuthority || t('currentUserConfiguration', { ns: 'labels' })}
                                </small>
                            )}
                        </div>
                        {isSaving && (
                            <div className="d-flex align-items-center text-primary">
                                <img src={waiting} alt="Saving..." style={{ width: '20px' }} className="me-2" />
                                <small>{t('saving', { ns: 'labels' })}</small>
                            </div>
                        )}
                    </div>
                </div>
                <div className="card-body">
                    {renderConfigFields()}

                    {LIS_ALERT_CONFIGURATION && (
                        <>
                            {/* Gestione ICA */}
                            <hr className="my-4" />
                            <h6 className="mb-3">
                                <i className="fa-solid fa-user-shield me-2 text-primary"></i>
                                Gestione ICA
                            </h6>
                            <div className="table-responsive mb-2">
                                <table className="table table-sm table-hover align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Ruolo organizzativo</th>
                                            <th className="text-center" style={{ width: 100 }}>Warn</th>
                                            <th className="text-center" style={{ width: 100 }}>Scheda</th>
                                            <th style={{ width: 40 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {icaRoles.map((row, idx) => {
                                            const usedIds = icaRoles.filter((_, i) => i !== idx).map(r => r.roleId).filter(Boolean);
                                            const availableOptions = organizationalRolesVoc.filter(o => !usedIds.includes(o.value));
                                            const selectedOption = organizationalRolesVoc.find(o => o.value === row.roleId) || null;
                                            return (
                                                <tr key={row._key}>
                                                    <td style={{ minWidth: 260 }}>
                                                        <Select
                                                            options={availableOptions}
                                                            value={selectedOption}
                                                            onChange={opt => setIcaRoles(prev => prev.map((r, i) => i === idx ? { ...r, roleId: opt?.value || '' } : r))}
                                                            placeholder="Seleziona ruolo..."
                                                            isClearable
                                                            isLoading={vocLoading}
                                                            menuPortalTarget={document.body}
                                                            styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }}
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="form-check form-switch d-flex justify-content-center m-0">
                                                            <input className="form-check-input" type="checkbox" checked={row.warn}
                                                                onChange={e => setIcaRoles(prev => prev.map((r, i) => i === idx ? { ...r, warn: e.target.checked } : r))} />
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="form-check form-switch d-flex justify-content-center m-0">
                                                            <input className="form-check-input" type="checkbox" checked={row.scheda}
                                                                onChange={e => setIcaRoles(prev => prev.map((r, i) => i === idx ? { ...r, scheda: e.target.checked } : r))} />
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <button className="btn btn-sm btn-outline-danger"
                                                            onClick={() => setIcaRoles(prev => prev.filter((_, i) => i !== idx))}
                                                            title="Rimuovi riga">
                                                            <i className="fa-solid fa-times"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <button className="btn btn-sm btn-outline-primary mb-3"
                                onClick={() => setIcaRoles(prev => [...prev, { _key: Date.now(), roleId: '', warn: false, scheda: false }])}
                                disabled={icaRoles.length >= organizationalRolesVoc.length && organizationalRolesVoc.length > 0}>
                                <i className="fa-solid fa-plus me-1"></i>Aggiungi ruolo
                            </button>
                            <div className="mb-4" style={{ maxWidth: 480 }}>
                                <label className="form-label small fw-semibold">
                                    <i className="fa-solid fa-file-medical me-1 text-primary"></i>
                                    Scheda da proporre (ICA)
                                </label>
                                <Select
                                    options={schedeVoc}
                                    value={schedeVoc.find(s => s.value === icaSchedaId) || null}
                                    onChange={opt => setIcaSchedaId(opt?.value || '')}
                                    placeholder="Seleziona scheda..."
                                    isClearable
                                    isLoading={schedeVocLoading}
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }}
                                />
                                <div className="form-text">Scheda proposta ai ruoli con "Scheda" abilitato quando le condizioni della regola sono soddisfatte.</div>
                            </div>

                            {/* Gestione Alert */}
                            <hr className="my-4" />
                            <h6 className="mb-3">
                                <i className="fa-solid fa-bell me-2 text-secondary"></i>
                                Gestione Alert
                            </h6>
                            <div className="table-responsive mb-2">
                                <table className="table table-sm table-hover align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Gruppo</th>
                                            <th className="text-center" style={{ width: 100 }}>Warn</th>
                                            <th className="text-center" style={{ width: 100 }}>Scheda</th>
                                            <th style={{ width: 40 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {alertGroups.map((row, idx) => {
                                            const usedIds = alertGroups.filter((_, i) => i !== idx).map(r => r.groupId).filter(Boolean);
                                            const availableOptions = groupsVoc.filter(o => !usedIds.includes(o.value));
                                            const selectedOption = groupsVoc.find(o => o.value === row.groupId) || null;
                                            return (
                                                <tr key={row._key}>
                                                    <td style={{ minWidth: 260 }}>
                                                        <Select
                                                            options={availableOptions}
                                                            value={selectedOption}
                                                            onChange={opt => setAlertGroups(prev => prev.map((r, i) => i === idx ? { ...r, groupId: opt?.value || '' } : r))}
                                                            placeholder="Seleziona gruppo..."
                                                            isClearable
                                                            isLoading={groupsVocLoading}
                                                            menuPortalTarget={document.body}
                                                            styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }}
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="form-check form-switch d-flex justify-content-center m-0">
                                                            <input className="form-check-input" type="checkbox" checked={row.warn}
                                                                onChange={e => setAlertGroups(prev => prev.map((r, i) => i === idx ? { ...r, warn: e.target.checked } : r))} />
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="form-check form-switch d-flex justify-content-center m-0">
                                                            <input className="form-check-input" type="checkbox" checked={row.scheda}
                                                                onChange={e => setAlertGroups(prev => prev.map((r, i) => i === idx ? { ...r, scheda: e.target.checked } : r))} />
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <button className="btn btn-sm btn-outline-danger"
                                                            onClick={() => setAlertGroups(prev => prev.filter((_, i) => i !== idx))}
                                                            title="Rimuovi riga">
                                                            <i className="fa-solid fa-times"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <button className="btn btn-sm btn-outline-primary mb-3"
                                onClick={() => setAlertGroups(prev => [...prev, { _key: Date.now(), groupId: '', warn: false, scheda: false }])}
                                disabled={alertGroups.length >= groupsVoc.length && groupsVoc.length > 0}>
                                <i className="fa-solid fa-plus me-1"></i>Aggiungi gruppo
                            </button>
                            <div className="mb-4" style={{ maxWidth: 480 }}>
                                <label className="form-label small fw-semibold">
                                    <i className="fa-solid fa-file-medical me-1 text-secondary"></i>
                                    Scheda da proporre (Alert)
                                </label>
                                <Select
                                    options={schedeVoc}
                                    value={schedeVoc.find(s => s.value === alertSchedaId) || null}
                                    onChange={opt => setAlertSchedaId(opt?.value || '')}
                                    placeholder="Seleziona scheda..."
                                    isClearable
                                    isLoading={schedeVocLoading}
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }}
                                />
                                <div className="form-text">Scheda proposta ai gruppi con "Scheda" abilitato quando le condizioni della regola sono soddisfatte.</div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="d-flex flex-column" style={{ height: '100vh' }}>
            {/* CSS for enhanced styling */}
            <style>
                {`
                .icon-box {
                    width: 60px;
                    height: 60px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                }
                .bg-primary-light {
                    background-color: rgba(13, 110, 253, 0.1) !important;
                }
                .bg-success-light {
                    background-color: rgba(25, 135, 84, 0.1) !important;
                }
                .bg-warning-light {
                    background-color: rgba(255, 193, 7, 0.1) !important;
                }
                .bg-info-light {
                    background-color: rgba(13, 202, 240, 0.1) !important;
                }
                .card {
                    border-radius: 12px;
                    border: 1px solid rgba(0,0,0,0.05);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.04);
                    transition: all 0.3s ease;
                }
                .card:hover {
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                    transform: translateY(-1px);
                }
                .form-check-input:checked {
                    background-color: #28a745;
                    border-color: #28a745;
                }
                .form-check-input:focus {
                    border-color: #28a745;
                    box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
                }
                .page-header {
                    margin-bottom: 2rem;
                    padding-bottom: 1rem;
                    border-bottom: 1px solid rgba(0,0,0,0.1);
                }
                .page-title {
                    color: #343a40;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                }
                .page-description {
                    font-size: 0.95rem;
                    margin-bottom: 0;
                }
                `}
            </style>

            {/* Breadcrumb managed by centralized component */}
            <div className="page-titles">
                <ol className="breadcrumb">
                </ol>
            </div>

            <div className="flex-grow-1 overflow-auto">
            <div className="container-fluid">
                {/* Page Header */}
                <div className="row">
                    <div className="col-12">
                        <div className="page-header">
                            <div className="row align-items-center">
                                <div className="col-sm-6">
                                    <h1 className="page-title">
                                        <i className="fa fa-cogs me-2"></i>
                                        {t('systemConfigurations', { ns: 'labels' })}
                                    </h1>
                                    <p className="page-description text-muted">
                                        {t('manageSystemConfigurations', { ns: 'labels' })}
                                    </p>
                                </div>
                                <div className="col-sm-6">
                                    <div className="text-sm-end">
                                        <div className="d-flex gap-3 justify-content-end align-items-center">
                                            {/* Unsaved Changes Indicator */}
                                            {(hasUnsavedChanges || computeHasAlertChanges()) && !isSaving && (
                                                <span
                                                    className="text-warning d-flex align-items-center"
                                                    style={{ fontSize: '12px' }}
                                                    title={t('unsavedChanges', { ns: 'labels' })}
                                                >
                                                    <i className="fas fa-circle me-1"></i>
                                                    {t('unsavedChanges', { ns: 'labels' })}
                                                </span>
                                            )}

                                            {/* Save Button */}
                                            <button
                                                type="button"
                                                className={`btn ${hasUnsavedChanges || computeHasAlertChanges() ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                onClick={saveConfiguration}
                                                disabled={(!hasUnsavedChanges && !computeHasAlertChanges()) || isSaving}
                                                title={t('saveConfiguration', { ns: 'labels' })}
                                            >
                                                <i className={`fa-solid ${isSaving ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>
                                                {t('save', { ns: 'labels' })}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistics Row */}
                {configuration && (
                    <div className="row mb-4">
                        <div className="col-xl-4 col-lg-4 col-md-6">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h2 className="mb-1 text-primary">
                                                {(() => {
                                                    const configData = configuration.config || JSON.parse(configuration.Config || '{}');
                                                    return Object.keys(configData).length;
                                                })()}
                                            </h2>
                                            <p className="mb-0 text-muted">
                                                {t('totalSettings', { ns: 'labels' }) !== 'totalSettings' ? t('totalSettings', { ns: 'labels' }) : 'Impostazioni Totali'}
                                            </p>
                                        </div>
                                        <div className="icon-box bg-primary-light">
                                            <i className="fa fa-cog text-primary"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-4 col-lg-4 col-md-6">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h2 className="mb-1 text-success">
                                                {(() => {
                                                    const configData = configuration.config || JSON.parse(configuration.Config || '{}');
                                                    return Object.values(configData).filter(v => v === true).length;
                                                })()}
                                            </h2>
                                            <p className="mb-0 text-muted">
                                                {t('enabled', { ns: 'labels' })}
                                            </p>
                                        </div>
                                        <div className="icon-box bg-success-light">
                                            <i className="fa fa-toggle-on text-success"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-4 col-lg-4 col-md-6">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h2 className="mb-1 text-warning">
                                                {(() => {
                                                    const configData = configuration.config || JSON.parse(configuration.Config || '{}');
                                                    return Object.values(configData).filter(v => v === false).length;
                                                })()}
                                            </h2>
                                            <p className="mb-0 text-muted">
                                                {t('disabled', { ns: 'labels' })}
                                            </p>
                                        </div>
                                        <div className="icon-box bg-warning-light">
                                            <i className="fa fa-toggle-off text-warning"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Configuration Content */}
                <div className="row justify-content-center">
                    <div className="col-12">
                        {renderConfiguration()}
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
};

export { Configurations_Screen }; 